import { defineStore } from 'pinia'
import axios from 'axios'

// Helper to get the auth token
const getAuthToken = () => {
    return localStorage.getItem('authToken')
}

// Create axios instance with auth interceptor
const api = axios.create()
api.interceptors.request.use(config => {
    const token = getAuthToken()
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

export const useFeaturesStore = defineStore('features', {
    state: () => ({
        features: [],
        isLoaded: false,
        isLoading: false,
        error: null,

        // Multi-tab synchronization
        broadcastChannel: null,
        broadcastMessageHandler: null,
        tabId: `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }),

    getters: {
        /**
         * Check if a specific feature is enabled
         * @param {string} featureCode - The code of the feature to check
         * @returns {boolean} - True if the feature is enabled, false otherwise
         */
        isEnabled: (state) => (featureCode) => {
            const feature = state.features.find(f => f.code === featureCode)
            return feature ? feature.isEnabled : false
        },

        /**
         * Get all enabled features
         * @returns {Array} - Array of enabled features
         */
        enabledFeatures: (state) => {
            return state.features.filter(f => f.isEnabled)
        },

        /**
         * Get a specific feature by code
         * @param {string} featureCode - The code of the feature
         * @returns {Object|null} - The feature object or null
         */
        getFeature: (state) => (featureCode) => {
            return state.features.find(f => f.code === featureCode) || null
        }
    },

    actions: {
        /**
         * Load features from the API
         * Only loads if not already loaded or if force refresh is requested
         */
        async loadFeatures(forceRefresh = false) {
            // Skip if already loaded and not forcing refresh
            if (this.isLoaded && !forceRefresh) {
                return this.features
            }

            // Skip if already loading
            if (this.isLoading) {
                return this.features
            }

            this.isLoading = true
            this.error = null

            try {
                const response = await api.get('/api/settings/features/')

                if (response?.data?.code === 'ok') {
                    this.features = response.data.data?.features || []
                    this.isLoaded = true

                    // Broadcast feature update to other tabs
                    this.broadcastFeaturesUpdate()
                } else {
                    throw new Error('Failed to load features')
                }
            } catch (error) {
                console.error('Error loading features:', error)
                this.error = error
                this.features = []
            } finally {
                this.isLoading = false
            }

            return this.features
        },

        /**
         * Refresh features from the API
         */
        async refreshFeatures() {
            return await this.loadFeatures(true)
        },

        /**
         * Initialize multi-tab synchronization using BroadcastChannel
         */
        initMultiTabSync() {
            // Only initialize if BroadcastChannel is supported
            if (typeof BroadcastChannel === 'undefined') {
                console.warn('BroadcastChannel not supported in this browser')
                return
            }

            // Clean up existing channel if any
            this.cleanup()

            // Create new broadcast channel
            this.broadcastChannel = new BroadcastChannel('features-sync')

            // Set up message handler
            this.broadcastMessageHandler = (event) => {
                const { type, data, sourceTabId } = event.data

                // Ignore messages from the same tab
                if (sourceTabId === this.tabId) {
                    return
                }

                switch (type) {
                    case 'FEATURES_UPDATED':
                        // Update features from another tab
                        this.features = data.features
                        this.isLoaded = true
                        break
                    case 'FEATURES_REFRESH_REQUESTED':
                        // Another tab requested a refresh
                        this.refreshFeatures()
                        break
                }
            }

            this.broadcastChannel.addEventListener('message', this.broadcastMessageHandler)
        },

        /**
         * Broadcast features update to other tabs
         */
        broadcastFeaturesUpdate() {
            if (this.broadcastChannel) {
                this.broadcastChannel.postMessage({
                    type: 'FEATURES_UPDATED',
                    data: {
                        features: JSON.parse(JSON.stringify(this.features))
                    },
                    sourceTabId: this.tabId
                })
            }
        },

        /**
         * Request all tabs to refresh features
         */
        requestRefreshAllTabs() {
            if (this.broadcastChannel) {
                this.broadcastChannel.postMessage({
                    type: 'FEATURES_REFRESH_REQUESTED',
                    sourceTabId: this.tabId
                })
            }
        },

        /**
         * Clean up resources (broadcast channel, etc.)
         */
        cleanup() {
            if (this.broadcastChannel) {
                if (this.broadcastMessageHandler) {
                    this.broadcastChannel.removeEventListener('message', this.broadcastMessageHandler)
                }
                this.broadcastChannel.close()
                this.broadcastChannel = null
                this.broadcastMessageHandler = null
            }
        },

        /**
         * Reset the store state
         */
        reset() {
            this.features = []
            this.isLoaded = false
            this.isLoading = false
            this.error = null
            this.cleanup()
        }
    }
})
